import { PageHeader } from '@/components/PageHeader'
import { CustomViewsProvider } from '@/components/Views/CustomViewsContext'
import { AppSidebar } from '@/components/app-sidebar'
import { AccessRestrictedOverlay } from '@/components/banner/AccessRestrictedOverlay'
import { CalDotComWrapper } from '@/components/cal-dot-com-wrapper/CalDotComWrapper'
import { DownloadManager } from '@/components/download-manager/DownloadManager'
import { PageProvider } from '@/components/page-context/PageContext'
import { usePageContext } from '@/components/page-context/context'
import { Separator } from '@/components/ui/separator'
import { SidebarInset, SidebarProvider, SidebarTrigger, useSidebar } from '@/components/ui/sidebar'
import { useIsMobile } from '@/hooks/use-mobile'
import { usePlanExpiration } from '@/hooks/usePlanExpiration'
import { isOrganizationLevelRoute, isProjectLevelRoute } from '@/lib/routerUtils'
import { cn } from '@/lib/utils'
import type { AuthStore } from '@/stores/auth'
import { useAuthStore } from '@/stores/authStore'
import { useProjectsStore } from '@/stores/projectsStore'
import { Outlet, useLocation, useRouter, useRouterState } from '@tanstack/react-router'
import { useEffect, useRef } from 'react'
import { RouteContextLayout, useRouteContext } from './TenantLayout'

interface SubscribeWithSelector<T> {
	subscribe: <U>(
		selector: (state: T) => U,
		listener: (selectedState: U, previousSelectedState: U) => void,
		options?: { fireImmediately?: boolean; equalityFn?: (a: U, b: U) => boolean },
	) => () => void
}

/**
 * Inner layout component — rendered INSIDE SidebarProvider so useSidebar() works.
 */
function AppLayout() {
	const isMobile = useIsMobile()
	const { hideHeader } = usePageContext()
	const { isOpen } = useSidebar()
	const { tenantId } = useRouteContext()

	// Resolve the subscription expiration status
	const expirationStatus = usePlanExpiration()
	const isAccessBlocked = expirationStatus.isAccessBlocked

	return (
		<>
			<AppSidebar />
			<SidebarInset className='flex flex-col min-w-0 w-full bg-sidebar relative'>
				{!hideHeader && (
					<header
						className={cn(
							'flex shrink-0 items-center transition-[width,height] ease-linear overflow-hidden',
							'min-h-14',
							isAccessBlocked && 'opacity-40 pointer-events-none select-none',
						)}>
						<div className={cn('flex grow items-center min-w-0', isMobile ? 'px-2' : 'px-4')}>
							<div className='flex items-center gap-2 shrink-0'>
								{(isMobile || !isOpen) && (
									<>
										<SidebarTrigger className='-ml-1' />
										<Separator orientation='vertical' className='h-4 mr-2' />
									</>
								)}
							</div>
							<div className='min-w-0 flex-1'>
								<PageHeader />
							</div>
						</div>
					</header>
				)}
				<div
					className={cn(
						'flex min-h-0 w-full flex-1 flex-col overflow-hidden page-content-wrapper',
						isMobile ? 'p-2' : '',
						isAccessBlocked && 'opacity-40 pointer-events-none select-none',
					)}>
					<Outlet key={tenantId} />
				</div>

				{/* Access Blocked Overlay */}
				{isAccessBlocked && <AccessRestrictedOverlay expirationDate={expirationStatus.expirationDate} />}
			</SidebarInset>
		</>
	)
}

function AuthenticatedLayout() {
	return (
		<CalDotComWrapper>
			<RouteContextLayout>
				<SidebarProvider>
					<CustomViewsProvider>
						<AppLayout />
						<DownloadManager />
					</CustomViewsProvider>
				</SidebarProvider>
			</RouteContextLayout>
		</CalDotComWrapper>
	)
}

export function RootLayout() {
	const isAuthenticated = useAuthStore(state => state.isAuthenticated)
	const router = useRouter()
	const location = useLocation()
	const isNavigatingRef = useRef(false)

	const pathname = location.pathname
	const isLoginPage = pathname === '/login'
	const isWelcomePage = pathname === '/welcome'

	// Detect 404 pages: since defaultNotFoundComponent is used, the catch-all route doesn't appear in matches
	// So we check if the route doesn't match known patterns
	const routerState = useRouterState()
	const onlyRootMatch = routerState.matches.length === 1 && routerState.matches[0]?.routeId === '__root__'
	const knownRoutePatterns = ['/login', '/welcome']
	const matchesKnownRoute = knownRoutePatterns.some(pattern => pathname.startsWith(pattern))

	const isOrgPath = pathname.startsWith('/orgs/')
	const isValidOrgRoute = isOrgPath && (isOrganizationLevelRoute(pathname) || isProjectLevelRoute(pathname))

	const isNotFoundPage =
		(onlyRootMatch && !matchesKnownRoute && pathname !== '/') ||
		(isOrgPath && !isValidOrgRoute && routerState.matches.length <= 2)

	const isAccessDeniedPath = pathname.includes('/access-denied')
	const isFullScreenAuthedContent = isAccessDeniedPath

	const isPageWithoutSidebar = isLoginPage || isWelcomePage || isNotFoundPage

	// Subscribe to auth state changes and navigate immediately when user becomes unauthenticated
	useEffect(() => {
		const unsubscribe = (useAuthStore as SubscribeWithSelector<AuthStore>).subscribe(
			state => state.isAuthenticated,
			(isAuth: boolean) => {
				if (isNavigatingRef.current) {
					// prevents infinite redirect loop
					return
				}

				const currentPath = location.pathname
				const isOnLoginPage = currentPath === '/login'
				const isOnWelcomePage = currentPath === '/welcome'

				const isOnPageWithoutSidebar = isOnLoginPage || isOnWelcomePage

				if (!isAuth && !isOnPageWithoutSidebar) {
					isNavigatingRef.current = true
					router
						.navigate({
							to: '/login',
							search: { redirect: currentPath },
							replace: true,
						})
						.finally(() => {
							setTimeout(() => {
								isNavigatingRef.current = false
							}, 500)
						})
				}
			},
		)

		return unsubscribe
	}, [router, location])

	useEffect(() => {
		const unsubscribe = (useAuthStore as SubscribeWithSelector<AuthStore>).subscribe(
			state => state.currentOrganizationId,
			async orgId => {
				if (orgId) {
					// Pass orgId: on switch this fires before navigate() updates the URL, so
					// resolveOrganizationId() (URL-first) would otherwise return the previous org.
					await useProjectsStore.getState().fetchProjects(undefined, undefined, undefined, orgId)
				}
			},
			{ fireImmediately: true },
		)

		return unsubscribe
	}, [])

	useEffect(() => {
		if (!isAuthenticated && !isPageWithoutSidebar && !isNavigatingRef.current) {
			isNavigatingRef.current = true
			router
				.navigate({
					to: '/login',
					search: { redirect: pathname },
					replace: true,
				})
				.finally(() => {
					setTimeout(() => {
						isNavigatingRef.current = false
					}, 500)
				})
		}
	}, [isAuthenticated, isPageWithoutSidebar, router, pathname])

	if (isLoginPage || isNotFoundPage) {
		return <Outlet />
	}

	if (isAuthenticated) {
		if (isWelcomePage) {
			return (
				<div className='flex flex-col min-h-screen w-screen overflow-hidden'>
					<PageProvider>
						<CustomViewsProvider>
							<RouteContextLayout>
								<Outlet />
							</RouteContextLayout>
						</CustomViewsProvider>
					</PageProvider>
				</div>
			)
		}

		if (isFullScreenAuthedContent) {
			return (
				<div className='min-h-screen w-screen overflow-hidden'>
					<PageProvider>
						<CustomViewsProvider>
							<SidebarProvider>
								<RouteContextLayout>
									<Outlet />
								</RouteContextLayout>
							</SidebarProvider>
						</CustomViewsProvider>
					</PageProvider>
				</div>
			)
		}

		return (
			<div className='h-svh w-screen overflow-hidden'>
				<PageProvider>
					<AuthenticatedLayout />
				</PageProvider>
			</div>
		)
	}

	// Fallback for unauthenticated users (should redirect to login)
	return <Outlet />
}
